// DeepSeek text/story provider contract.
import type { PromptRequest } from '../prompts/types.js';
import { formatDirectorErrorForUi } from '../prompts/errors.js';
import {
  parseAndValidateDirectorOutput,
  parseProviderJson,
  validateDirectorOutput,
  type OutputSchemaId,
} from '../prompts/validate.js';
import type { z } from 'zod';

export const TEXT_PROVIDER = 'deepseek' as const;

export interface TextGenerationRequest {
  provider: typeof TEXT_PROVIDER;
  model: string;
  messages: Array<{ role: 'system' | 'user'; content: string }>;
  json: boolean;
  maxTokens: number;
  temperature: number;
  outputSchemaId: string;
}

export function buildTextGenerationRequest(
  prompt: PromptRequest,
  systemPreamble?: string,
): TextGenerationRequest {
  if (prompt.provenance.provider !== 'deepseek') {
    throw new Error('Text generation requires deepseek provider contract');
  }
  const schemaHint = prompt.structuredOutput
    ? ` Return ONLY JSON matching schema: ${prompt.outputSchemaId}.`
    : '';
  return {
    provider: TEXT_PROVIDER,
    model: 'deepseek-flash',
    messages: [
      {
        role: 'system',
        content: (systemPreamble ?? 'Return schema-valid JSON when structured output is requested.') + schemaHint,
      },
      { role: 'user', content: prompt.instructions },
    ],
    json: prompt.structuredOutput,
    maxTokens: prompt.budget.outputTokens,
    temperature: 0.9,
    outputSchemaId: prompt.outputSchemaId,
  };
}

/** Structured recoverable validation — preferred pre-persist / pre-UI path. */
export function parseTextProviderJson<T>(
  schemaId: OutputSchemaId,
  schema: z.ZodType<T>,
  rawText: string,
) {
  return parseAndValidateDirectorOutput(schemaId, schema, rawText);
}

export function parseTextProviderStructured<T>(
  schemaId: OutputSchemaId,
  schema: z.ZodType<T>,
  raw: unknown,
) {
  const result = validateDirectorOutput(schemaId, schema, raw);
  if (result.ok) return result;
  return {
    ok: false as const,
    error: result.error,
    uiMessage: formatDirectorErrorForUi(result.error),
  };
}

export { parseProviderJson };
